/**
 * What a failed call throws.
 *
 * It throws rather than returning a value, and that is wf-12 U-12: a `401` that came back as
 * `[]` is a screen that renders "no deployments" over an authentication failure, and it is
 * indistinguishable from the truth. The Fleet Console's whole job is telling somebody which
 * of our deployments is in trouble; a surface that answers "none" when it means "I could not
 * ask" is worse than one that is down, because nobody goes and looks.
 */
export class FleetApiError extends Error {
  readonly status: number
  readonly url: string

  constructor(message: string, status: number, url: string) {
    super(message)
    this.name = 'FleetApiError'
    this.status = status
    this.url = url
  }
}

/**
 * The one a caller is meant to catch: the principal is missing or no longer accepted, and the
 * answer is to sign in again. `reauthenticate` is a field rather than an `instanceof` check
 * because an error crossing a module boundary in a bundler that duplicated the module would
 * fail the instance check and be swallowed as "some other error", which lands back on the
 * blank screen this exists to prevent.
 */
export class FleetApiAuthError extends FleetApiError {
  readonly reauthenticate = true

  constructor(message: string, status: number, url: string) {
    super(message, status, url)
    this.name = 'FleetApiAuthError'
  }
}

/** True for the error a sign-in surface should react to, however it reached us. */
export function needsReauthentication(error: unknown): boolean {
  return Boolean(error && typeof error === 'object' && 'reauthenticate' in error && error.reauthenticate)
}
